// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define terms. The audience knows most of these; the ones that matter
 * are the two that are routinely used to mean different things — "recall" and
 * "overlap" — and the one this tool takes a position on, which is why rank is
 * never summed.
 */

export const GLOSSARY: Record<string, { title: string; body: string }> = {
  chunk: {
    title: 'Chunk',
    body: 'One piece of a document, embedded and stored as a single vector. Retrieval returns chunks, so a chunk is the smallest thing your pipeline can ever hand to the model.',
  },
  overlap: {
    title: 'Overlap',
    body: 'How many tokens the end of one chunk repeats at the start of the next. It exists so an answer that straddles a boundary is whole in at least one chunk — and it costs you storage and duplicate hits.',
  },
  hit: {
    title: 'Answers retrieved',
    body: 'How many of the passages you marked came back at all, out of how many you marked. A passage counts as retrieved when one of the returned chunks overlaps it by at least one character — deliberately not "what fraction of it came back", because people mark a paragraph when they meant a sentence and a fractional score would then be measuring the selection rather than the chunking.',
  },
  budget: {
    title: 'Context budget',
    body: 'The tokens you are willing to paste into your model per query. The number of results is derived from it — floor(budget ÷ chunk size) — so every chunk size is judged at the same price. At a fixed number of results a bigger chunk simply retrieves more text, and "recall rises with chunk size" becomes arithmetic instead of a finding.',
  },
  chance: {
    title: 'Chance level',
    body: 'Exactly what random selection would score at this setting: the probability that picking k chunks at random from the corpus catches one that touches your marked passage. When the score is close to this line, nothing has been measured. It is computed in closed form, not simulated.',
  },
  retrieved: {
    title: 'Retrieved tokens',
    body: 'The total size of the top results, in your model’s tokens. It is the price of the recall beside it: what your pipeline pays per query in context, and in money.',
  },
  rank: {
    title: 'Rank',
    body: 'Where the first chunk containing your marked passage appeared. Never compare it across chunk sizes on its own — bigger chunks mean fewer of them, so a good rank is easier to get. It is always shown beside the number of chunks.',
  },
  strategy: {
    title: 'Strategy',
    body: 'How the document is cut. Fixed-token ignores structure entirely; recursive prefers paragraph, then line, then sentence boundaries; heading-aware cuts at headings and stamps each chunk with the heading trail above it.',
  },
  tokenizer: {
    title: 'Tokenizer',
    body: 'What turns text into the units your embedding model counts. Chunk size is meaningless without one — the same paragraph can be 1,024 tokens to one model and 1,180 to another.',
  },
  'static-embedding': {
    title: 'Static embedding',
    body: 'A model with no attention and no network to run: each token is a row in a table, and the sentence vector is a weighted mean of those rows. Much weaker than a transformer at retrieval, and fast enough to re-embed a whole corpus while you drag a slider.',
  },
  idf: {
    title: 'IDF weighting',
    body: 'Rare words count more than common ones when averaging token vectors. Without it, function words dominate and a plain mean points at nothing in particular. The weights here are fitted on your paragraphs once, so they are identical for every strategy being compared.',
  },
};

export function mountGlossary(root: HTMLElement): () => void {
  let tip: HTMLDivElement | null = null;

  const close = () => {
    tip?.remove();
    tip = null;
  };

  const onClick = (event: Event) => {
    const target = (event.target as HTMLElement | null)?.closest?.('.glossary-link') as HTMLElement | null;
    if (!target) {
      close();
      return;
    }
    event.preventDefault();
    const term = target.dataset.term ?? '';
    const entry = GLOSSARY[term];
    if (!entry) return;
    close();
    tip = document.createElement('div');
    tip.className = 'glossary-tip';
    tip.setAttribute('role', 'tooltip');
    const strong = document.createElement('strong');
    strong.textContent = entry.title;
    tip.append(strong, document.createTextNode(entry.body));
    document.body.append(tip);
    const rect = target.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    tip.style.width = `${width}px`;
    tip.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
    const below = rect.bottom + 8;
    tip.style.top = below + tip.offsetHeight > window.innerHeight ? `${Math.max(12, rect.top - tip.offsetHeight - 8)}px` : `${below}px`;
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  };

  document.addEventListener('pointerdown', onClick);
  document.addEventListener('keydown', onKey);
  window.addEventListener('resize', close);
  void root;

  return () => {
    document.removeEventListener('pointerdown', onClick);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', close);
    close();
  };
}
